import { create } from 'zustand';
import { User } from '../types';

const ACCESS_TOKEN_KEY = 'accessToken';
const USER_KEY = 'authUser';
const LAST_ACTIVITY_KEY = 'lastActivity';

interface AuthStore {
  user: User | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  initialize: () => void;
  login: (payload: { user: User; accessToken: string }) => void;
  logout: () => void;
  updateActivity: () => void;
}

const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  isAuthenticated: false,
  isInitialized: false,
  initialize: () => {
    const storedToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);

    // Check if session has expired (2.5 hours of inactivity - longer than JWT expiration)
    const now = Date.now();
    const sessionTimeout = 2.5 * 60 * 60 * 1000; // 2.5 hours in milliseconds

    if (lastActivity && (now - parseInt(lastActivity)) > sessionTimeout) {
      // Session expired, clear everything
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      set({ user: null, isAuthenticated: false, isInitialized: true });
      return;
    }

    if (storedToken && storedUser) {
      try {
        const parsedUser: User = JSON.parse(storedUser);
        set({ user: parsedUser, isAuthenticated: true, isInitialized: true });
        // Update activity timestamp
        localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
      } catch (error) {
        console.error('Failed to parse stored user data', error);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        set({ user: null, isAuthenticated: false, isInitialized: true });
      }
    } else {
      // No stored auth, initialization complete
      set({ isInitialized: true });
    }
  },
  login: ({ user, accessToken }) => {
    const now = Date.now();
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    set({ user, isAuthenticated: true });
  },
  logout: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    set({ user: null, isAuthenticated: false });
  },
  updateActivity: () => {
    const now = Date.now();
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
  },
}));

export default useAuthStore;